import React, { useCallback, useEffect, useRef, useState } from "react";
import { DialogLayout } from "../../../../ui/layouts/DialogLayout";
import { Button } from "../../../../ui/components/Button";
import { IconButton } from "../../../../ui/components/IconButton";
import { TextField } from "../../../../ui/components/TextField";
import { Select } from "../../../../ui/components/Select";
import { Loader } from "../../../../ui/components/Loader";
import {
  FeatherAlertTriangle,
  FeatherCheckCircle,
  FeatherFileText,
  FeatherInfo,
  FeatherUpload,
  FeatherUploadCloud,
  FeatherX,
} from "@subframe/core";
import { useToast } from "../../../../shared/presentation/components";
import { LocationPicker } from "../../../../shared/presentation/components/LocationPicker";
import { useActiveLocations } from "../../../../shared/hooks/useActiveLocations";
import {
  extractLut,
  uploadLutV2,
  type LutExtractResult,
  type LutSummary,
} from "../../data/api/lutApi";

const ACCEPTED = ".pdf,.png,.jpg,.jpeg";

/** "2026-27" → { from: "2026-04-01", to: "2027-03-31" } */
const fyBounds = (fy: string): { from: string; to: string } | null => {
  const m = fy.match(/^(\d{4})\s*-\s*(\d{2}|\d{4})$/);
  if (!m) return null;
  const start = parseInt(m[1], 10);
  return { from: `${start}-04-01`, to: `${start + 1}-03-31` };
};

export interface UploadLutModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  summary: LutSummary | null;
  onUploaded: () => void;
}

export const UploadLutModal: React.FC<UploadLutModalProps> = ({
  open,
  onOpenChange,
  summary,
  onUploaded,
}) => {
  const { showError, showSuccess } = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [extraction, setExtraction] = useState<LutExtractResult | null>(null);
  const [extractFailed, setExtractFailed] = useState(false);

  const [arn, setArn] = useState("");
  const [fy, setFy] = useState("");
  const [validFrom, setValidFrom] = useState("");
  const [validTo, setValidTo] = useState("");
  const [uploading, setUploading] = useState(false);

  /**
   * ZOUR-90 — which GST registration this LUT is filed for.
   *
   * An LUT is filed per GSTIN, so on a client with several registrations the
   * upload has to say which one. The extracted GSTIN answers it in the normal
   * case (the picker is preselected from `matched_location`); the picker is
   * there for the case it cannot — an unreadable document, or a GSTIN that
   * matches none of ours.
   *
   * Hidden entirely for a single-registration client: there is nothing to
   * choose, and the upload files under the primary exactly as it always has.
   */
  const {
    locations: activeLocations,
    hasMultiple: hasMultipleLocations,
    primary: primaryLocation,
  } = useActiveLocations();
  const [locationId, setLocationId] = useState<string | null>(null);
  // "I checked — upload anyway" confirmation, required only when the file
  // doesn't look like an LUT acknowledgement. This is the ONLY hard gate.
  const [notLutConfirmed, setNotLutConfirmed] = useState(false);

  const applyFy = useCallback((value: string) => {
    setFy(value);
    const bounds = fyBounds(value);
    if (bounds) {
      setValidFrom(bounds.from);
      setValidTo(bounds.to);
    }
  }, []);

  // Reset each time the modal opens; default to the current financial year.
  useEffect(() => {
    if (!open) return;
    setFile(null);
    setExtraction(null);
    setExtractFailed(false);
    setNotLutConfirmed(false);
    setArn("");
    if (fileInputRef.current) fileInputRef.current.value = "";
    applyFy(summary?.current_fy ?? "");
  }, [open, summary?.current_fy, applyFy]);

  const runExtract = useCallback(async (chosen: File) => {
    setExtracting(true);
    setExtraction(null);
    setExtractFailed(false);
    try {
      const form = new FormData();
      form.append("file", chosen);
      const result = await extractLut(form);
      setExtraction(result);
      const ex = result.extracted;
      if (ex.arn_number) setArn(ex.arn_number);
      if (ex.financial_year) {
        setFy(ex.financial_year);
        const bounds = fyBounds(ex.financial_year);
        setValidFrom(ex.valid_from || bounds?.from || "");
        setValidTo(ex.valid_to || bounds?.to || "");
      } else {
        if (ex.valid_from) setValidFrom(ex.valid_from);
        if (ex.valid_to) setValidTo(ex.valid_to);
      }

      // ZOUR-90: the document's own GSTIN decides the registration whenever it
      // matches one of ours. Only preselect — never lock — because the paper can
      // be misread and the admin is the better authority.
      if (result.matched_location?.zoho_location_id) {
        setLocationId(result.matched_location.zoho_location_id);
      }
    } catch (err) {
      setExtractFailed(true);
    } finally {
      setExtracting(false);
    }
  }, []);

  // ZOUR-90: default to the primary, the registration an unlocated document
  // belongs to. Only until the user or the extraction says otherwise.
  useEffect(() => {
    if (locationId || !primaryLocation?.zoho_location_id) return;
    setLocationId(primaryLocation.zoho_location_id);
  }, [primaryLocation?.zoho_location_id, locationId]);

  const handleFileChosen = useCallback(
    (chosen: File | null) => {
      setFile(chosen);
      setExtraction(null);
      setExtractFailed(false);
      setNotLutConfirmed(false);
      if (chosen) void runExtract(chosen);
    },
    [runExtract],
  );

  const handleUpload = useCallback(async () => {
    if (!file) {
      showError("Please choose a LUT file to upload");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("arn_number", arn.trim());
      form.append("financial_year", fy.trim());
      form.append("valid_from", validFrom);
      form.append("valid_to", validTo);
      // ZOUR-90. `gstin` is what the paper said and is stored on the row; the
      // server falls back to matching it when no location was picked.
      if (locationId) {
        form.append("location_id", locationId);
      }
      if (extraction?.extracted.gstin) {
        form.append("gstin", extraction.extracted.gstin);
      }
      await uploadLutV2(form);
      showSuccess("LUT uploaded");
      onUploaded();
      onOpenChange(false);
    } catch (err) {
      showError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  }, [
    file,
    arn,
    fy,
    validFrom,
    validTo,
    locationId,
    extraction,
    onUploaded,
    onOpenChange,
    showError,
    showSuccess,
  ]);

  const busy = extracting || uploading;

  // "Looks like an LUT" when the AI classified it as an LUT acknowledgement.
  // Anything else (invoice / other / unclassified) trips the confirm gate.
  const looksLikeLut =
    !!extraction &&
    (extraction.extracted.document_type === "lut_acknowledgement" ||
      extraction.extracted.is_lut_document === true);
  const notLut = !!extraction && !looksLikeLut;

  const matched = !!extraction && extraction.company_match.status === "match";
  const mismatch = !!extraction && extraction.company_match.status === "mismatch";

  // A LUT already on file for the chosen FY — uploading replaces it.
  /**
   * ZOUR-90 — "you already have one for this year" is now a question about this
   * REGISTRATION, not about the company.
   *
   * `covered_fys` counts every registration's LUTs together, so with two
   * registrations it warned "uploading this will replace it" when uploading the
   * FIRST Bengaluru LUT — which replaces nothing. The supersede is scoped to
   * the registration, so this notice has to be too, or it contradicts what the
   * upload actually does.
   */
  const duplicateFy =
    fy.trim() !== "" &&
    (hasMultipleLocations
      ? (summary?.locations ?? []).some((row) => {
          const isThisOne = (locationId ?? "") === (row.zoho_location_id ?? "")
            || (locationId === null && row.is_primary);

          return isThisOne
            && row.active_lut !== null
            && (row.active_lut.financial_year ?? "").trim() === fy.trim();
        })
      : (summary?.covered_fys.includes(fy.trim()) ?? false));

  // Two-step reveal: the form appears only after the AI has read the document
  // (or failed to — manual entry is never blocked by an AI outage).
  const showForm = !!file && !extracting && (!!extraction || extractFailed);

  // Quiet per-field hints when the user's value diverges from the document.
  // Deliberately subtle — editing a prefilled value is often the user
  // correcting the read, so only the "wrong file" case gets the loud red.
  const docHint = (userVal: string, docVal: string | null | undefined): string | null =>
    docVal && userVal.trim() !== docVal.trim() ? `Document shows ${docVal}` : null;
  const arnHint = extraction ? docHint(arn, extraction.extracted.arn_number) : null;
  const fyHint = extraction ? docHint(fy, extraction.extracted.financial_year) : null;
  const validFromHint = extraction ? docHint(validFrom, extraction.extracted.valid_from) : null;
  const validToHint = extraction ? docHint(validTo, extraction.extracted.valid_to) : null;

  const fyOptions = summary?.fy_options ?? [];
  // If the document (or a previous selection) carries a year outside the
  // current ±5 list, still offer it so the AI prefill isn't silently dropped.
  const extraFy =
    fy.trim() !== "" && !fyOptions.some((o) => o.fy === fy.trim())
      ? fy.trim()
      : null;

  return (
    <DialogLayout
      open={open}
      onOpenChange={(next) => {
        if (!busy) onOpenChange(next);
      }}
      className="min-w-[520px] max-w-[560px]"
    >
      <div className="flex w-full flex-col">
        {/* Header */}
        <div className="flex w-full items-start justify-between gap-4 border-b border-solid border-neutral-border p-6">
          <div className="flex flex-col items-start gap-1">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Upload LUT
            </span>
            <span className="text-body font-body text-subtext-color">
              Drop your LUT document — we'll read the details for you to check.
            </span>
          </div>
          <IconButton
            size="small"
            icon={<FeatherX />}
            onClick={() => {
              if (!busy) onOpenChange(false);
            }}
            disabled={busy}
          />
        </div>

        {/* Body */}
        <div className="flex w-full flex-col gap-4 p-6">
          {/* Step 1 — the document is all the user provides up front; the
              form below is revealed once the AI has read it. */}
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED}
            className="hidden"
            onChange={(e) => handleFileChosen(e.target.files?.[0] ?? null)}
          />
          {!file ? (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={busy}
              className="flex w-full flex-col items-center gap-2 rounded-md border border-dashed border-neutral-border bg-neutral-50 px-6 py-10"
            >
              <FeatherUploadCloud className="h-6 w-6 text-subtext-color" />
              <span className="text-body-bold font-body-bold text-default-font">
                Choose your LUT document
              </span>
              <span className="text-caption font-caption text-subtext-color">
                PDF or image, up to 10MB
              </span>
            </button>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="neutral-secondary"
                icon={<FeatherFileText />}
                onClick={() => fileInputRef.current?.click()}
                disabled={busy}
              >
                Replace file
              </Button>
              <span className="text-body font-body text-subtext-color">
                {file.name}
              </span>
            </div>
          )}

          {extracting && (
            <div className="flex w-full items-center gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 p-4">
              <Loader size="small" />
              <span className="text-body font-body text-subtext-color">
                Reading the document…
              </span>
            </div>
          )}

          {/* Read verdicts */}
          {!extracting && matched && !notLut && (
            <div className="flex w-full items-start gap-3 rounded-md border border-solid border-success-200 bg-success-50 p-4">
              <FeatherCheckCircle className="mt-0.5 flex-none text-success-700" />
              <span className="text-body font-body text-default-font">
                This looks like your company's LUT. We've filled in the details
                below — please check and upload.
              </span>
            </div>
          )}
          {/* (a) Wrong-file gate — the ONLY hard block in this modal. */}
          {!extracting && notLut && (
            <div className="flex w-full flex-col gap-1 rounded-md border border-solid border-error-300 bg-error-50 p-4">
              <div className="flex items-start gap-3">
                <FeatherAlertTriangle className="mt-0.5 flex-none text-error-600" />
                <span className="text-body-bold font-body-bold text-default-font">
                  This doesn't look like an LUT acknowledgement
                </span>
              </div>
              <span className="pl-8 text-body font-body text-subtext-color">
                It looks like{" "}
                {extraction?.extracted.document_type === "invoice"
                  ? "an invoice"
                  : "a different document"}
                {extraction?.extracted.legal_name
                  ? ` from ${extraction.extracted.legal_name}`
                  : ""}
                . Please check you picked the right file.
              </span>
              <label className="flex cursor-pointer items-center gap-2 pl-8 pt-1">
                <input
                  type="checkbox"
                  checked={notLutConfirmed}
                  onChange={(e) => setNotLutConfirmed(e.target.checked)}
                  disabled={busy}
                />
                <span className="text-body font-body text-default-font">
                  I checked — this is the right file, upload anyway
                </span>
              </label>
            </div>
          )}
          {/* (b) Company mismatch — warn only, never blocks. */}
          {!extracting && mismatch && !notLut && (
            <div className="flex w-full items-start gap-3 rounded-md border border-solid border-error-300 bg-error-50 p-4">
              <FeatherAlertTriangle className="mt-0.5 flex-none text-error-600" />
              <span className="text-body font-body text-default-font">
                This document appears to be for a different company
                {extraction?.extracted.legal_name
                  ? ` (${extraction.extracted.legal_name})`
                  : ""}
                {" "}— please check.
              </span>
            </div>
          )}
          {!extracting && extractFailed && file && (
            <div className="flex w-full items-start gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 p-4">
              <FeatherAlertTriangle className="mt-0.5 flex-none text-subtext-color" />
              <span className="text-body font-body text-subtext-color">
                We couldn't read this document. Please fill in the details
                below.
              </span>
            </div>
          )}

          {showForm && (
            <>
              {/* (d) Replace notice — a LUT is already on file for this FY. */}
              {duplicateFy && (
                <div className="flex w-full items-start gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 p-4">
                  <FeatherInfo className="mt-0.5 flex-none text-subtext-color" />
                  <span className="text-body font-body text-default-font">
                    You already have a LUT on file for {fy.trim()}
                    {hasMultipleLocations
                      ? ` for ${
                          (summary?.locations ?? []).find(
                            (row) =>
                              (locationId ?? "") === (row.zoho_location_id ?? "") ||
                              (locationId === null && row.is_primary),
                          )?.name ?? "this registration"
                        }`
                      : ""}
                    . Uploading this one will replace it.
                  </span>
                </div>
              )}

              {/* ZOUR-90 — which GST registration this LUT is filed for.
                  Shown only when the company has more than one; a
                  single-registration client sees the form it always saw. */}
              {hasMultipleLocations && (
                <div className="flex w-full flex-col gap-2 rounded-md border border-solid border-neutral-border bg-default-background px-4 py-3">
                  <LocationPicker
                    locations={activeLocations}
                    value={locationId}
                    disabled={busy}
                    onChange={setLocationId}
                  />
                  <span className="text-caption font-caption text-subtext-color">
                    {extraction?.matched_location?.name
                      ? `Read from the document's GSTIN (${extraction.matched_location.name}). Change it if that is wrong.`
                      : "An LUT is filed per GSTIN — choose the registration this one covers."}
                  </span>
                </div>
              )}

              {/* Details — quiet hints call out fields that differ from the doc */}
              <div className="flex flex-wrap items-end gap-4">
                <div className="flex w-56 flex-none flex-col gap-1">
                  <TextField
                    className="h-auto w-full"
                    variant="filled"
                    label="LUT / ARN number"
                  >
                    <TextField.Input
                      placeholder="e.g. AD3306..."
                      value={arn}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setArn(e.target.value)
                      }
                    />
                  </TextField>
                  {arnHint && (
                    <span className="text-[10px] leading-[14px] font-caption text-subtext-color">
                      {arnHint}
                    </span>
                  )}
                </div>
                <div className="flex w-44 flex-none flex-col gap-1">
                  <Select
                    className="w-full"
                    variant="filled"
                    label="Financial year"
                    placeholder="Select year"
                    value={fy}
                    onValueChange={applyFy}
                    disabled={busy}
                  >
                    {fyOptions.map((opt) => (
                      <Select.Item key={opt.fy} value={opt.fy}>
                        {opt.fy}
                        {opt.is_current ? " (this year)" : ""}
                      </Select.Item>
                    ))}
                    {extraFy && (
                      <Select.Item key={extraFy} value={extraFy}>
                        {extraFy}
                      </Select.Item>
                    )}
                  </Select>
                  {fyHint && (
                    <span className="text-[10px] leading-[14px] font-caption text-subtext-color">
                      {fyHint}
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-caption font-caption text-subtext-color">
                    Valid from
                  </span>
                  <input
                    type="date"
                    value={validFrom}
                    onChange={(e) => setValidFrom(e.target.value)}
                    className="h-8 rounded-md border border-solid border-neutral-border bg-default-background px-3 text-body font-body text-default-font"
                  />
                  {validFromHint && (
                    <span className="text-[10px] leading-[14px] font-caption text-subtext-color">
                      {validFromHint}
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-caption font-caption text-subtext-color">
                    Valid to
                  </span>
                  <input
                    type="date"
                    value={validTo}
                    onChange={(e) => setValidTo(e.target.value)}
                    className="h-8 rounded-md border border-solid border-neutral-border bg-default-background px-3 text-body font-body text-default-font"
                  />
                  {validToHint && (
                    <span className="text-[10px] leading-[14px] font-caption text-subtext-color">
                      {validToHint}
                    </span>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex w-full items-center justify-end gap-3 border-t border-solid border-neutral-border p-6">
          <Button
            variant="neutral-secondary"
            onClick={() => {
              if (!busy) onOpenChange(false);
            }}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            icon={<FeatherUpload />}
            onClick={() => void handleUpload()}
            loading={uploading}
            disabled={busy || !file || (notLut && !notLutConfirmed)}
          >
            Upload LUT
          </Button>
        </div>
      </div>
    </DialogLayout>
  );
};
